/**
 * Keeping installed apps current (mobile M5; extended in M8).
 *
 * An installed app — the Android APK, or the iPhone home-screen app with its
 * cached shell — runs the bundle it was built with against the newest server.
 * API changes are additive, but when one can't be, the server raises
 * `min_client` (/api/version) and an older bundle says "update needed"
 * instead of failing oddly.
 */

import { apiBase, isNative } from "./index";

/** This bundle's own version, stamped by Vite from package.json. */
export const CLIENT_VERSION: string = __CLIENT_VERSION__;
export const BUILD_ID: string = __BUILD_ID__;

/** -1, 0 or 1, comparing dotted numeric versions ("0.10.0" > "0.9.3"). */
export function compareVersions(a: string, b: string): number {
  const pa = a.split(/[-+]/)[0]!.split(".").map((n) => Number.parseInt(n, 10) || 0);
  const pb = b.split(/[-+]/)[0]!.split(".").map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

export type VersionInfo = {
  app: string;
  api: string;
  build: string;
  min_client?: string;
  android?: { version_name?: string; version_code?: number; url?: string; min_version_code?: number } | null;
};

/** What the server says about versions, or null when it can't be reached. */
export async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    const res = await fetch(`${apiBase()}/api/version`, { cache: "no-store" });
    return res.ok ? ((await res.json()) as VersionInfo) : null;
  } catch {
    return null;
  }
}

/** Whether this bundle is older than the server supports. */
export function tooOld(info: VersionInfo | null, version = CLIENT_VERSION): boolean {
  return Boolean(info?.min_client && compareVersions(version, info.min_client) < 0);
}

/**
 * Register the service worker (src/sw.js): production web builds only. The
 * Android app loads its files from the APK and has no worker; dev has none so
 * Vite's own reloading is never fighting a cache.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!import.meta.env.PROD || isNative() || typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  try {
    return await navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" });
  } catch {
    return null; // a refused registration costs the offline shell, nothing else
  }
}

/* ------------------------------------------------- the Android release (M8) */

/** versionCode as app/build.gradle stamps it: major·10000 + minor·100 + patch. */
export function versionCodeOf(version: string): number {
  const [a = 0, b = 0, c = 0] = version
    .split(/[-+]/)[0]!
    .split(".")
    .map((n) => Number.parseInt(n, 10) || 0);
  return a * 10000 + b * 100 + c;
}

export type AndroidRelease = NonNullable<VersionInfo["android"]> & {
  version_name: string;
  version_code: number;
  min_version_code: number;
  url: string;
  sha256: string;
  size: number;
  released: string;
  notes?: string[];
};

export type UpdateDecision =
  | { kind: "none" }
  /** A newer build exists; the app works without it. */
  | { kind: "offer"; release: AndroidRelease }
  /** This build is below the floor (min_version_code, or the server's min_client): update to go on. */
  | { kind: "wall"; release: AndroidRelease | null };

/** What the Android app should say about updates, from /api/version. */
export function decideUpdate(info: VersionInfo | null, installedCode: number, version = CLIENT_VERSION): UpdateDecision {
  const release = (info?.android ?? null) as AndroidRelease | null;
  const belowFloor = release !== null && installedCode < release.min_version_code;
  if (belowFloor || tooOld(info, version)) return { kind: "wall", release };
  if (release && release.version_code > installedCode) return { kind: "offer", release };
  return { kind: "none" };
}

/** This APK's versionCode, or null in a browser. Falls back to the bundle's own version. */
export async function installedVersionCode(): Promise<number | null> {
  if (!isNative()) return null;
  try {
    const { App } = await import("@capacitor/app");
    const code = Number.parseInt((await App.getInfo()).build, 10);
    if (Number.isFinite(code) && code > 0) return code;
  } catch {
    /* fall through */
  }
  return versionCodeOf(CLIENT_VERSION);
}

type ApkInstaller = {
  canInstall(): Promise<{ granted: boolean }>;
  openInstallSettings(): Promise<void>;
  install(options: { url: string; sha256: string; name: string }): Promise<void>;
  addListener(event: "progress", fn: (e: { bytes: number; total: number }) => void): Promise<{ remove(): Promise<void> }>;
};
let installer: ApkInstaller | null = null;
let registeringInstaller: Promise<void> | null = null;

async function registerInstaller(): Promise<void> {
  registeringInstaller ??= import("@capacitor/core").then(
    ({ registerPlugin }) => {
      installer = registerPlugin<ApkInstaller>("ApkInstaller");
    },
    () => undefined,
  );
  await registeringInstaller;
}

export type InstallOutcome = "installing" | "not-permitted" | "checksum-mismatch" | "download-failed" | "unsupported";

/** Open Android's "install unknown apps" screen for MyDeck. */
export async function openInstallSettings(): Promise<void> {
  if (!isNative()) return;
  await registerInstaller();
  await installer?.openInstallSettings().catch(() => undefined);
}

/**
 * Download the release, check its SHA-256 in Java, and open Android's
 * installer. The URL is a PATH from the server, resolved against the origin
 * this APK was built for — never a host the manifest names.
 */
export async function downloadAndInstall(
  release: AndroidRelease,
  onProgress?: (fraction: number) => void,
): Promise<InstallOutcome> {
  if (!isNative()) return "unsupported";
  await registerInstaller();
  if (!installer) return "unsupported";
  const url = new URL(release.url, apiBase() || window.location.origin).toString();
  const name = release.url.split("/").pop() || "mydeck-update.apk";
  let handle: { remove(): Promise<void> } | undefined;
  try {
    if (onProgress) {
      handle = await installer.addListener("progress", ({ bytes, total }) => {
        if (total > 0) onProgress(Math.min(1, bytes / total));
      });
    }
    await installer.install({ url, sha256: release.sha256, name });
    return "installing";
  } catch (err) {
    const code = (err as { code?: string } | null)?.code;
    if (code === "UNIMPLEMENTED") return "unsupported";
    if (code === "checksum-mismatch" || code === "not-permitted") return code;
    return "download-failed";
  } finally {
    void handle?.remove();
  }
}
